import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';

import { Bonus, Slider } from '@atlas/components';
import { AccountManager, Bus, SiteConfigManager } from '@atlas/core';
import { DAY, Icon, Label, Loader, Timer, ToggleCheckbox } from '@atlas/ui';

import { BASE_PATH, bonusStatuses, bonusTypes } from 'common/constants';
import { useIsRtl } from 'hooks/useIsRtl';
import { CDN_TYPE_CRM, imageUrlCDN } from 'src/common/utils';
import useLanguage from 'src/hooks/useLanguage';

import { useDepositBonusContext } from './DepositBonusContext';

const { ACTIVATED, IN_PROGRESS } = bonusStatuses;
const { DEPOSIT, NO_DEPOSIT, RELOAD_DEPOSIT, RELOAD_NO_DEPOSIT } = bonusTypes;

const timerLabels = {
  days: 'timer_d',
  hours: 'timer_h',
  minutes: 'timer_m',
};

const hasFinanceData = bonus =>
  Boolean(bonus?.financeData) || Boolean(bonus?.financeDataList?.length);

const DepositBonuses = () => {
  const { alpha3Code } = useLanguage();
  const hideSportBonuses = SiteConfigManager.getConfig('config.config.general.hideSportBonuses');
  const isRtl = useIsRtl();
  const currency = AccountManager.getAccountCurrency();

  const [activeBonusId, setActiveBonusId] = useState(null);
  const [autoDisabledBonusId, setAutoDisabledBonusId] = useState(null);
  const wasSelectionClearedByUserRef = useRef(false);

  const {
    activatedBonus,
    activeBonuses = [],
    amountToGetBonus,
    bonusesToShow = [],
    isBonusesDisabled,
    isLoading,
    selectedBonus,
    setSelectedBonus,
  } = useDepositBonusContext();
  // for re-render slider
  const sliderKey = useMemo(() => bonusesToShow.map(({ id }) => id).join('-'), [bonusesToShow]);
  const bonuses = useMemo(
    () => (activatedBonus ? [activatedBonus] : bonusesToShow),
    [activatedBonus, bonusesToShow],
  );

  const filteredBonuses = useMemo(() => {
    const depositBonuses = bonuses.filter(
      bonus => bonus?.bonusType === DEPOSIT || bonus?.bonusType === RELOAD_DEPOSIT,
    );

    const blockedProductTypes = activeBonuses
      .filter(
        item =>
          item.status === IN_PROGRESS &&
          (item?.bonus?.bonusType === NO_DEPOSIT ||
            item?.bonus?.bonusType === RELOAD_NO_DEPOSIT ||
            item?.bonus?.productType === 'CASINO' ||
            item?.bonus?.productType === 'SPORTSBOOK'),
      )
      .map(item => item.bonus.productType);

    const sourceBonuses = blockedProductTypes.length
      ? depositBonuses.filter(bonus => !blockedProductTypes.includes(bonus.productType))
      : depositBonuses;

    return sourceBonuses.filter(bonus => bonus?.id != null && hasFinanceData(bonus));
  }, [activeBonuses, bonuses]);

  const hasSingleBonus = filteredBonuses.length === 1;
  const dir = useMemo(() => (isRtl ? 'rtl' : 'ltr'), [isRtl]);

  const handleDepositComplete = useCallback(() => {
    wasSelectionClearedByUserRef.current = false;
    setAutoDisabledBonusId(null);
  }, []);

  useEffect(() => {
    Bus.on(Bus.events.profile.depositComplete, handleDepositComplete);

    return () => Bus.off(Bus.events.profile.depositComplete, handleDepositComplete);
  }, [handleDepositComplete]);

  useEffect(() => {
    if (activatedBonus?.id) {
      wasSelectionClearedByUserRef.current = false;
      if (activatedBonus.disabled) {
        setActiveBonusId(null);
        if (selectedBonus?.id === activatedBonus.id) {
          setSelectedBonus(null);
        }
      } else {
        setActiveBonusId(activatedBonus.id);
        if (selectedBonus?.id !== activatedBonus.id) {
          setSelectedBonus(activatedBonus);
        }
      }
      return;
    }

    if (isBonusesDisabled) {
      setActiveBonusId(null);
      return;
    }

    if (selectedBonus?.id) {
      wasSelectionClearedByUserRef.current = false;
      setActiveBonusId(selectedBonus.id);
      return;
    }

    if (filteredBonuses.length && !autoDisabledBonusId && !wasSelectionClearedByUserRef.current) {
      const firstBonus = filteredBonuses[0];
      setActiveBonusId(firstBonus.id);
      setSelectedBonus(firstBonus);
    }
  }, [
    activatedBonus,
    autoDisabledBonusId,
    filteredBonuses,
    isBonusesDisabled,
    selectedBonus,
    setSelectedBonus,
  ]);

  useEffect(() => {
    if (activatedBonus) return;

    if (amountToGetBonus > 0 && activeBonusId) {
      setAutoDisabledBonusId(activeBonusId);
      setActiveBonusId(null);
      return;
    }

    if (amountToGetBonus === 0 && !activeBonusId && autoDisabledBonusId) {
      const bonusToRestore = filteredBonuses.find(bonus => bonus.id === autoDisabledBonusId);
      if (bonusToRestore) {
        wasSelectionClearedByUserRef.current = false;
        setActiveBonusId(bonusToRestore.id);
        setSelectedBonus(bonusToRestore);
      }
      setAutoDisabledBonusId(null);
    }
  }, [
    activatedBonus,
    activeBonusId,
    autoDisabledBonusId,
    filteredBonuses,
    amountToGetBonus,
    setSelectedBonus,
  ]);

  const handleSelectBonus = useCallback(
    bonus => {
      if (bonus?.status === ACTIVATED) {
        wasSelectionClearedByUserRef.current = false;
        setActiveBonusId(bonus.id);
        setSelectedBonus(bonus);
        return;
      }

      const isActive = selectedBonus?.id === bonus.id;
      wasSelectionClearedByUserRef.current = isActive;
      setActiveBonusId(isActive ? null : bonus.id);
      setAutoDisabledBonusId(null);
      setSelectedBonus(isActive ? null : bonus);
    },
    [selectedBonus, setSelectedBonus],
  );

  const sliderConfig = useMemo(
    () => ({
      autoHeight: false,
      autoplay: false,
      className: 'deposit-bonus__slider',
      dir,
      loop: false,
      nextBtn: <Slider.NextBtn className="deposit-bonus-slider__next-arrow" />,
      prevBtn: <Slider.PrevBtn className="deposit-bonus-slider__prev-arrow" />,
      slidesPerView: 'auto',
      spaceBetween: 8,
    }),
    [dir],
  );

  if (isLoading) {
    return <Loader className="profile-deposit__loader" />;
  }

  if (!filteredBonuses.length) {
    return null;
  }

  const renderBonus = bonus => {
    const finance = bonus.financeDataList.find(item => item.currency === currency);
    const bonusImage = bonus?.localizedImageInfoList?.find(
      item => item.language.toLowerCase() === alpha3Code.toLowerCase(),
    );

    const imageLink = bonusImage?.imageLink
      ? `${imageUrlCDN(bonusImage.imageLink, CDN_TYPE_CRM)}`
      : `${BASE_PATH}/static/assets/images/gift.svg`;
    const bonusTitle = <h4 className="deposit-bonus-title">{bonus.name}</h4>;
    const isActive = selectedBonus?.id === bonus.id;

    const bonusDescription = (
      <div className="deposit-bonus-description">
        <Label
          message="deposit_min_amount"
          params={{
            currency: bonus.currency,
            minBonusAmount: finance?.minDeposit,
          }}
        />
      </div>
    );

    const getTimer = () => {
      if (bonus.expirationTime) return bonus.expirationTime;

      if (
        bonus?.status === bonusStatuses.ACTIVATED ||
        bonus?.bonusStatus === bonusStatuses.ACTIVE
      ) {
        return bonus.createTime != null ? bonus.createTime + 3 * DAY : (bonus.endTime ?? null);
      }

      return null;
    };

    const disableBlock = (
      <div className="deposit-bonus-disable">
        <Icon className="disabled-icon" name="lock" />
        <Label className="deposit-bonus-disable-title" message="deposit_bonus_unavailable_title" />
        <Label
          className="deposit-bonus-disable-description"
          message="deposit_bonus_unavaliable_message"
        />
      </div>
    );

    const timer = getTimer() && (
      <div className="deposit-timer">
        <Icon className="clock" height={16} name="time" width={16} />
        <Timer labels={timerLabels} separator=":" showSeconds={false} time={getTimer()} />
      </div>
    );

    const bonusLabel = !hideSportBonuses && (
      <Label className="deposit-bonus-label" message={`bonus_product_${bonus.productType}`} />
    );

    return (
      <div
        className={classNames('deposit-bonus', {
          ['deposit-bonus--single']: hasSingleBonus,
        })}
        key={bonus.id}
      >
        <Bonus
          active={isActive}
          description={bonusDescription}
          disabled={bonus.disabled || false}
          disabledBlock={disableBlock}
          image={imageLink}
          label={bonusLabel}
          timer={timer}
          title={bonusTitle}
          toggleCheckbox={
            <ToggleCheckbox
              checked={isActive}
              disabled={isBonusesDisabled}
              onChange={() => handleSelectBonus(bonus)}
            />
          }
        />
      </div>
    );
  };

  return (
    <div
      className={classNames('deposit-bonus-wrapper', {
        single: hasSingleBonus,
      })}
    >
      <div
        className={classNames('deposit-bonus-header', {
          single: hasSingleBonus,
        })}
      >
        <Label className="deposit-bonus-header-title" message="toggle_to_select" />
        <Label
          className="deposit-bonus-header-description"
          message="deposit_to_activate"
          params={{
            currency,
            minBonusAmount: 3000,
          }}
        />
      </div>

      {hasSingleBonus ? (
        renderBonus(filteredBonuses[0])
      ) : (
        <div className="slider-wrapper">
          <Slider key={sliderKey} {...sliderConfig}>
            {filteredBonuses.map(bonus => (
              <Slider.Slide key={bonus.id}>{renderBonus(bonus)}</Slider.Slide>
            ))}
          </Slider>
        </div>
      )}
    </div>
  );
};

export default DepositBonuses;
